/**
 * Metric evaluation: the one place a displayed number is produced.
 */

import { parse, topLevelTerms, type Node } from './expr.js';
import { evaluateRange, type RangeScope } from './range.js';
import type {
  Metric,
  MetricResult,
  Pack,
  Range,
  Step,
  UnitId,
} from './schema.js';
import { buildScope, defaultParams, scenarioById, type ScopeState } from './scope.js';
import { provenanceOf } from './provenance.js';
import { roundToSigFigs } from './units.js';

const astCache = new Map<string, Node>();

export function parseCached(expr: string): Node {
  let n = astCache.get(expr);
  if (!n) {
    n = parse(expr);
    astCache.set(expr, n);
  }
  return n;
}

export function metricById(pack: Pack, id: string): Metric {
  const m = pack.metrics.find((x) => x.id === id);
  if (!m) throw new Error(`Unknown metric "${id}"`);
  return m;
}

export function evaluateMetric(
  pack: Pack,
  metric: Metric,
  state: ScopeState,
): MetricResult {
  const scope = buildScope(pack, state);
  const node = parseCached(metric.expr);
  const prov = provenanceOf(node, pack);

  // If any input this metric depends on is not public, the metric is blocked.
  // Rendering it as zero would be a fabrication.
  const blockedBy = prov.inputIds.filter((id) => scope.undisclosed[id]);
  if (blockedBy.length > 0) {
    const first = scope.undisclosed[blockedBy[0]!]!;
    return {
      metricId: metric.id,
      scenarioId: state.scenarioId,
      value: first,
      unit: metric.unit,
      sigFigs: prov.sigFigs,
      confidence: prov.confidence,
      inputClosure: prov.inputIds,
      sourceIds: prov.sourceIds,
      blocked: true,
    };
  }

  const value = evaluateRange(node, scope.values);

  const result: MetricResult = {
    metricId: metric.id,
    scenarioId: state.scenarioId,
    value,
    unit: metric.unit,
    sigFigs: prov.sigFigs,
    confidence: prov.confidence,
    inputClosure: prov.inputIds,
    sourceIds: prov.sourceIds,
    blocked: false,
  };

  const series = evaluateSeries(pack, metric, scope.values);
  if (series) result.series = series;

  return result;
}

/** Per-year values for metrics that declare a series. `t` is years elapsed. */
export function evaluateSeries(
  pack: Pack,
  metric: Metric,
  values: RangeScope,
): Range[] | undefined {
  if (!metric.series) return undefined;
  const horizonVal = values[metric.series.horizonYearsInput];
  if (horizonVal === undefined) return undefined;
  const horizon = Math.round(
    typeof horizonVal === 'number' ? horizonVal : horizonVal.mid,
  );
  if (!Number.isFinite(horizon) || horizon <= 0 || horizon > 200) return undefined;

  const node = parseCached(metric.series.perYearExpr);
  const out: Range[] = [];
  for (let t = 0; t < horizon; t++) {
    out.push(evaluateRange(node, { ...values, t }));
  }
  void pack;
  return out;
}

/** Evaluate one metric across every scenario it applies to. */
export function evaluateAcrossScenarios(
  pack: Pack,
  metric: Metric,
  base: Omit<ScopeState, 'scenarioId'>,
): Record<string, MetricResult> {
  const out: Record<string, MetricResult> = {};
  for (const s of pack.scenarios) {
    if (metric.appliesTo && !metric.appliesTo.includes(s.id)) continue;
    const scenario = scenarioById(pack, s.id);
    out[s.id] = evaluateMetric(pack, metric, {
      scenarioId: s.id,
      // Params are per-scenario; fall back to that scenario's own defaults.
      params: { ...defaultParams(scenario), ...base.params },
      overrides: base.overrides,
    });
  }
  return out;
}

/**
 * Intermediate values for the top-level terms of a multi-term expression, so a
 * reader can walk the arithmetic instead of trusting one collapsed answer.
 */
export function stepsFor(
  metric: Metric,
  pack: Pack,
  state: ScopeState,
  labelFor: (id: string) => string,
): Step[] {
  const scope = buildScope(pack, state);
  const node = parseCached(metric.expr);
  const terms = topLevelTerms(node);
  if (terms.length < 2) return [];

  return terms.map(({ sign, node: term }) => {
    const v = evaluateRange(term, scope.values);
    const signed: Range =
      sign === 1 ? v : { lo: -v.hi, mid: -v.mid, hi: -v.lo };
    return {
      label: describeTerm(term, labelFor),
      value: signed,
      unit: metric.unit,
    };
  });
}

function describeTerm(n: Node, labelFor: (id: string) => string): string {
  const ids: string[] = [];
  const collect = (node: Node): void => {
    switch (node.t) {
      case 'ref':
        ids.push(node.id);
        break;
      case 'op':
        collect(node.l);
        collect(node.r);
        break;
      case 'neg':
        collect(node.x);
        break;
      case 'call':
        node.args.forEach(collect);
        break;
      default:
        break;
    }
  };
  collect(n);
  if (ids.length === 0) return 'constant';
  return ids.map(labelFor).join(' × ');
}

/** Round a range for display. Applied once, at the render boundary. */
export function roundRange(r: Range, sigFigs: number): Range {
  return {
    lo: roundToSigFigs(r.lo, sigFigs),
    mid: roundToSigFigs(r.mid, sigFigs),
    hi: roundToSigFigs(r.hi, sigFigs),
  };
}

export function unitOf(metric: Metric): UnitId {
  return metric.unit;
}
